/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import {
    HSAdministeredDrug,
    HSDrug, HSPackedMedication, HSPatient, HSTestResult, UserDto,
    PatchApplicationActivityDto,
    PatchApplicationActivityTypeDto,
    PatchApplicationDto,
    PatchApplicationScheduledActivityDto,
    ReasonCode
} from 'server-openapi';
import styled from 'styled-components';
import { useCurrentUser } from '../../../../core/authn/UserProvider';
import {useMap, useStore} from '../../../../core/storage/hooks/UseStore';
import { Button } from '../../../../kit/Button';
import { Dialog } from '../../../../kit/Dialog';
import { Form } from '../../../../kit/Forms/Form';
import { FormGroup } from '../../../../kit/Forms/FormGroup';
import { RadioGroup } from '../../../../kit/Forms/RadioGroup';
import { TextArea } from '../../../../kit/Forms/TextArea';
import { Layout } from '../../../../kit/Layout';
import { Text } from '../../../../kit/Text';
import { colors } from '../../../../kit/Theme/Theme';
import { toasts } from '../../../../kit/Toasts/Toaster';
import { useSyncCenter } from '../../../../syncstream/SyncCenterProvider';
import { useApiUtils } from '../../../../syncstream/utils/hooks/useApiUtils';
import { DrugDetailLeftPanel } from '../drugDetails/DrugDetailLeftPanel';
import {
    MedicationInformationProps,
    SecondCheckDialogContainer
} from '../medicationInformation/MedicationInformationBox';
import { PatchLocationDTO, PatchLocations } from './PatchLocations';
import {PatchBodyBgImage, PatchLocationButton} from './PatchLocationVisual';
import { PatchUtils } from './PatchUtils';
import { getAdministrationError } from '../medicationInformation/MedicationInformationBox';
import { DateUtils } from '../../../../core/utils/dateUtils';
import {useGroupPermissions} from "../../../../core/authz/PermissionsProvider";
import { DebugHover } from '../../../../core/utils/debughover';
import {User} from "../../../../core/authn/user-management";
import {ResidentDetailsUtils} from "../../../../syncstream/utils/ResidentDetailsUtils";
import {SyncPatchApplications} from "../../../../syncstream/SyncPatchApplication";
import {PatchApplicationStatusDto} from "server-openapi/dist/generated-sdk/api";
import {getPatchLocationForApplication} from "../../../../syncstream/utils/PatchApplicationUtils";
import {SecondCheckData} from "../MedicationListsTabbedRouter/MedicationListsTabbedRouter";
import {EnqueuedDrugCreateData} from "../../../../syncstream/SyncRounds";
import {canManage} from "../medicationInformation/PatchApplicationItems";


export async function processPatchAdministration(
    data: PatchApplicationFormFields,
    patchLocation: PatchLocationDTO, 
    user: User,
    isLate: boolean,
    patient: HSPatient, 
    packedMedication: HSPackedMedication, 
    hsUsers: ReadonlyMap<string, UserDto>,
    residentDetails: ResidentDetailsUtils,
    patchApplicationService: SyncPatchApplications,
    drugStore: ReadonlyMap<string,  HSDrug>,
    patchApplication?: PatchApplicationDto,
    patchApplicationScheduledActivity?: PatchApplicationScheduledActivityDto,
    reasonCode?: ReasonCode,
    secondCheckData?: SecondCheckData,
    onAdminister?: (
        drugs: HSAdministeredDrug[],
        doseTimestamp: string,
        testResults?: HSTestResult[],
    ) => Promise<EnqueuedDrugCreateData | undefined> 
) {
    const notes = data.notes ? [data.notes] : [];

    let status = reasonCode;
    if (!status) {
        status =  isLate ? ReasonCode.DosedLate : ReasonCode.Dosed;
    }
    let activityType = data.activityType ?? PatchApplicationActivityTypeDto.Applied; 
    
    if (status == ReasonCode.Dosed || status == ReasonCode.DosedLate) {
        // Relates the clinical system id of the administered drug to the packed med it was created from.
        const secondCheckInfo = secondCheckData?.secondCheckableMedication[packedMedication.hsId!];
        const secondCheckUser = hsUsers?.get(secondCheckInfo?.hsId?.toString() ?? '-1');
        if (activityType === PatchApplicationActivityTypeDto.Applied || activityType === PatchApplicationActivityTypeDto.Reapplied) {
           const drug = drugStore.get(packedMedication.drugHsId!.toString());
           const administeredDrug = residentDetails.generateAdministeredDrug(
               packedMedication,
               drug!,
               status!,
               notes,
               user,
               secondCheckInfo?.userName,
               secondCheckInfo?.hsId,
               packedMedication.dosage,
           );
           const queuedDrugs = await onAdminister?.([administeredDrug],
               packedMedication && 'doseTimestamp' in packedMedication
                   ? packedMedication?.doseTimestamp ?? ''
                   : '',
           );

           // TODO:  Add second check.
           if (!queuedDrugs || queuedDrugs.administeredDrugs.length == 0) {
               return undefined;
           }
           if (activityType === PatchApplicationActivityTypeDto.Applied) {
                const pm = packedMedication as HSPackedMedication;
                await patchApplicationService.enqueue.applyPatch({
                        patientId: patient.hsId,
                        wardId: patient.facility,
                        scheduledStart: pm?.doseTimestamp ??  DateUtils.fromDate(new Date()),
                        clientCreatedAt: DateUtils.fromDate(new Date()), 
                        clientUpdatedAt: DateUtils.fromDate(new Date())
                    }, {
                        administeredDrugClinicalSystemId: queuedDrugs!.administeredDrugs[0].clinicalSystemId,
                        // administeredAdHocDrugId: administeredAdHocDrug.clinicalSystemId,
                        confirmationUser: {
                            subjectId: secondCheckUser?.subjectId,
                            firstName: secondCheckUser?.firstName, 
                            familyName: secondCheckUser?.familyName
                        },
                    },
                    {
                        activityType: activityType,
                        patchLocation: {
                            description: patchLocation.PatchArea?.toString(),
                            number: patchLocation.LocationNumber?.toString()
                        },
                        clientCreatedAt: DateUtils.fromDate(new Date()),
                        clientUpdatedAt: DateUtils.fromDate(new Date()),
                        clientUser: {
                            
                            subjectId: user.profile.sub,
                            firstName: user.profile.given_name,
                            familyName: user.profile.family_name
                        }, 
                        comment: notes.join('\n')
                    }, queuedDrugs.round!.clinicalSystemId!);

            }
            else {
                await patchApplicationService.enqueue.reapplyPatch(patchApplication!, {
                        administeredDrugClinicalSystemId: queuedDrugs.administeredDrugs[0].clinicalSystemId,
                        // administeredAdHocDrugId: administeredAdHocDrug.clinicalSystemId, 
                        confirmationUser: {
                            subjectId: secondCheckUser?.subjectId,
                            firstName: secondCheckUser?.firstName,
                            familyName: secondCheckUser?.familyName
                        },
                    },
                    {
                        activityType: activityType,
                        patchLocation: {
                            description: patchLocation.PatchArea?.toString(),
                            number: patchLocation.LocationNumber?.toString()
                        },
                        clientCreatedAt: DateUtils.fromDate(new Date()),
                        clientUpdatedAt: DateUtils.fromDate(new Date()), 
                        clientUser: {
                            subjectId: user.profile.sub,
                            firstName: user.profile.given_name,
                            familyName: user.profile.family_name
                        },
                        comment: notes.join('\n')
                    }, queuedDrugs.round!.clinicalSystemId!);
            }
        }
        else {
            await patchApplicationService.enqueue.managePatch(patchApplication!, {
                activityType: activityType!,
                scheduledActivityId: patchApplicationScheduledActivity?.hsId,
                clientCreatedAt: DateUtils.fromDate(new Date()),
                clientUpdatedAt: DateUtils.fromDate(new Date()),
                clientUser: {
                    subjectId: user.profile.sub,
                    firstName: user.profile.given_name,
                    familyName: user.profile.family_name
                },
                comment: notes.join('\n')
            });
        }
    }
}

export interface PatchApplicationDialogProps extends MedicationInformationProps {
    drug: HSDrug;
    confirmationInitials?: string;
    confirmationUserId?: number;
    
    // This is the activity type that is to be selected by default.
    // It is only used if viewOnly is false.
    activityType?: PatchApplicationActivityTypeDto;
    
    // This is the patch application that this application belongs to.
    // Nil means that it is a new application.
    patchApplication?: PatchApplicationDto;
    
    // The activity we are currently viewing.  Only set when viewOnly is true.
    patchApplicationActivity?: PatchApplicationActivityDto;
    
    // This is the activity we are scheduled to perform.
    // If this is set, then activityType is ignored and the activityType is determined from the scheduled activity. 
    patchApplicationScheduledActivity?: PatchApplicationScheduledActivityDto;
    
    // The label to show on the button.
    label: string;
    
    // The list of options to show in the radio group.
    availableOptions?: PatchApplicationActivityTypeDto[]
    
    closeDialog?: () => void;
    hideButton?: boolean;
    open?: boolean;
    viewOnly: boolean;
    isLate: boolean;
    hasBeenDosed: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function PatchApplicationDialog(props: PatchApplicationDialogProps) {
    const [open, setOpen] = useState(props.open ?? false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (isMountedRef.current) {
            setOpen(props.open ?? false);
        }
    }, [props.open]);
    
    function onOpen() {
        const administrationError = getAdministrationError(props);
        if (administrationError) {
            toasts.error(administrationError);
            return;
        }
        setOpen(true);
    }

    const closePatchDialog = () => {
        if (isMountedRef.current) {
            setOpen(false);
        }
        props.closeDialog?.();
    };

    const getAdministerBtnDisabilityStatus = (): boolean => {
        return !props.administrationPermissions.canSightPatch;
    };
    
    return (
        <>
            {!props.hideButton &&
                (props.viewOnly ?
                    <ActionButton data-testid="patch-action-button" onClick={onOpen} disabled={getAdministerBtnDisabilityStatus()}>
                        VIEW
                    </ActionButton>
                :
                    <>
                        {/*<IconDotMenu style={{ cursor: 'pointer' }} onClick={onOpen} />*/}
                        <ActionButton data-testid="patch-action-button" onClick={onOpen} disabled={getAdministerBtnDisabilityStatus()}>
                            {props.label}
                        </ActionButton>
                    </>
                )
            }
            <Dialog
                lazy
                closeButtonColor={'#fff'}
                open={open ?? false}
                onRequestClose={closePatchDialog}
                size="full"
            >
                <DialogContainer horizontal gap={1} data-testid="patch-action-dialog">
                    <DrugDetailLeftPanel
                        drug={props.drug}
                        packedMedication={props.packedMedication}
                        facilityGroupId={props.facilityGroupId}
                        patient={props.patient}
                    />
                    <AdministerPatchApplicationForm
                        {...props}
                        onAdminister={async (aDrugs: HSAdministeredDrug[], doseTimestamp: string) => {
                            const newDrug = await props.onAdminister?.(aDrugs, doseTimestamp);
                            if (isMountedRef.current) {
                                setOpen(false);
                            }
                            return newDrug;
                        }}
                        closeDialog={closePatchDialog}
                    />
                </DialogContainer>
            </Dialog>
        </>
    );
}

interface PatchApplicationFormFields {
    activityType?: PatchApplicationActivityTypeDto;
    notes?: string;
    patchLocation?: PatchLocationDTO;
}

// eslint-disable-next-line max-lines-per-function
function AdministerPatchApplicationForm(props: PatchApplicationDialogProps) {
    const [selectedPatchLocation, setSelectedPatchLocation] = useState<PatchLocationDTO|undefined>(getPatchLocationForApplication(props.patchApplicationActivity?.patchLocation ?? props.patchApplication?.patchLocation));
    const apiUtils = useApiUtils();
    const services = useSyncCenter();
    const drugStore = useStore(services.drugs.store).store;
    const user = useCurrentUser();
    const hsUserMap = useMap(services.users);
    const isRemovingPatch = props.activityType === PatchApplicationActivityTypeDto.Removed ||
        props.activityType === PatchApplicationActivityTypeDto.FallenOff;
    
    const recentPatchLocations = PatchUtils.getRecentPatchLocations(apiUtils.patients, props.patient);
    const groupPermissions = useGroupPermissions();
    const canRecordNote = groupPermissions.canRecordNote;
    const isUpdate = props.patchApplication?.status === PatchApplicationStatusDto.Applied || props.patchApplication?.status === PatchApplicationStatusDto.Reapplied;
    const isNew = !props.patchApplication;
    const secondCheckInfo = props.secondCheckData?.secondCheckableMedication[props.nimAvailableDrug ? props.nimAvailableDrug.id! : props.packedMedication!.hsId!];
    const requiresSecondCheck = apiUtils.residentDetails.isMedicationSecondCheckable(props.facilityGroupId, props.administrationPermissions.canAdministerPatchMedicationExcludingS8 ?? false, props.packedMedication ?? props.nimAvailableDrug!) && !(secondCheckInfo?.userName);
    const canReapply = canManage(props.administrationPermissions, props.drug, false);

    const form = useForm<PatchApplicationFormFields>({
        defaultValues: {
            activityType: (props.activityType ?? (props.patchApplication ? PatchApplicationActivityTypeDto.Sighted : PatchApplicationActivityTypeDto.Applied)), 
            notes: props.patchApplication?.comment ?? '',
            patchLocation: selectedPatchLocation
        },
    });
    const currentActivity = form.watch('activityType');

    useEffect(() => {
        form.setValue('activityType', props.activityType ?? props.patchApplicationActivity?.activityType ?? PatchApplicationActivityTypeDto.Applied);
        form.setValue('notes',props.patchApplicationActivity?.comment ?? '');
    }, [props.activityType]);
     
    useEffect(() => {
        if (props.viewOnly || props.activityType === PatchApplicationActivityTypeDto.Removed || props.activityType === PatchApplicationActivityTypeDto.FallenOff) {
            setSelectedPatchLocation(getPatchLocationForApplication(props.patchApplicationActivity?.patchLocation ?? props.patchApplication?.patchLocation));
        } else if (isRemovingPatch || isNew) {
            setSelectedPatchLocation(getPatchLocationForApplication(props.patchApplication?.patchLocation));
        }
    }, [props.previousDosedDrugs]);

    useEffect(() => {
        form.trigger('notes');
        form.setValue('patchLocation', selectedPatchLocation)
    }, [selectedPatchLocation]);

    async function onSubmit(data: PatchApplicationFormFields) {
        if (data.notes && !canRecordNote) {
            toasts.error('You do not have permission to record notes');
            return;
        }
        await processPatchAdministration(data, selectedPatchLocation!, user, props.isLate, props.patient, props.packedMedication!, hsUserMap, apiUtils.residentDetails, services.patchApplications.service, drugStore, props.patchApplication, props.patchApplicationScheduledActivity, props.status, props.secondCheckData, props.onAdminister);        
        props.closeDialog!();
    }

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Layout gap={1}>
                <PatchLocationSelectorFullBody
                    currentStatus={props.patchApplication?.status ?? "Unknown"}
                    isViewOnly={props.patchApplication?.status === PatchApplicationStatusDto.Applied || props.patchApplication?.status === PatchApplicationStatusDto.Reapplied || props.activityType === PatchApplicationActivityTypeDto.Removed || props.viewOnly}
                    selectedPatchLocation={selectedPatchLocation}
                    recentlyPatchedLocations={recentPatchLocations}
                    setSelectedPatchLocation={setSelectedPatchLocation}
                />
                <ApplicationStatusSection
                    isUpdateOrRemovePatch={ !props.viewOnly }
                    currentStatus={ props.patchApplication?.status }
                    activityType={ props.patchApplicationActivity?.activityType  ?? props.activityType }
                    isNew={isNew}
                    formControl={form.control}
                    canReapply={canReapply}
                />
                <PatchNotes
                    isHidden={props.viewOnly || !props.administrationPermissions.canRecordNote}
                    isRecentlyPatched={
                        !isRemovingPatch && !!selectedPatchLocation && recentPatchLocations.includes(selectedPatchLocation)
                    }
                    activityType={currentActivity}
                />
                {
                    props.viewOnly && <>
                        <WhiteText
                            size="medium">{getLabelText(props.patchApplicationActivity?.activityType)} at {DateUtils.toDisplayString(props.patchApplicationActivity?.clientUpdatedAt)} by {props.patchApplicationActivity?.clientUser?.firstName} {props.patchApplicationActivity?.clientUser?.familyName}</WhiteText>
                        {
                            props.patchApplicationActivity?.confirmationUser &&
                            <WhiteText size="medium">Confirmed
                                by {props.patchApplicationActivity?.confirmationUser?.firstName} {props.patchApplicationActivity?.confirmationUser?.familyName}</WhiteText>
                        }
                    </>
                }

                <DebugHover name={'PatchButton'}
                            content={`props.viewOnly=${props.viewOnly}<BR>form.formState.isSubmitSuccessful=${form.formState.isSubmitSuccessful}<BR>selectedPatchLocation=${selectedPatchLocation}<BR>props.isUpdatePatch=${isUpdate}<BR>isRemovingPatch=${isRemovingPatch}`}>
                    {
                        props.viewOnly ?
                            <Button
                                style={{width: '250px', margin: "1.5em"}}
                                data-testid="patch-dialog-action-button"
                                type="button"
                                fullWidth
                                onClick={() => {
                                    props.closeDialog!();
                                }}
                            >
                                {props.label}
                            </Button>
                            :
                            requiresSecondCheck && (currentActivity ===  PatchApplicationActivityTypeDto.Applied || currentActivity ===  PatchApplicationActivityTypeDto.Reapplied) ?
                                <SecondCheckDialogContainer {...props} /> :
                            <Button
                                style={{width: '250px', margin: "1.5em"}}
                                data-testid="patch-dialog-action-button"
                                type="submit"
                                disabled={
                                    form.formState.isSubmitSuccessful ||
                                    (!selectedPatchLocation && !isUpdate && !isRemovingPatch)
                                }
                            >
                                {props.label}
                            </Button>
                    }
                </DebugHover>
            </Layout>
        </Form>
    );
}

export function PatchLocationSelectorFullBody(props: {
    currentStatus: string;
    isViewOnly?: boolean;
    selectedPatchLocation?: PatchLocationDTO;
    recentlyPatchedLocations: (PatchLocationDTO | undefined)[];
    setSelectedPatchLocation: (selectedPatchLocation: PatchLocationDTO) => void;
}) {
    const { selectedPatchLocation, recentlyPatchedLocations, setSelectedPatchLocation } = props;



    return (
        <>
            {  props.isViewOnly && <WhiteText size="large" weight="bold">{getLabelText(props.currentStatus)}</WhiteText> }
            {!props.isViewOnly && <WhiteText size="large" weight="bold">Select area</WhiteText> }
            <Layout>
                <PatchBodyBgImage>
                    {PatchLocations.map((value, index) => (
                        <PatchLocationButton
                            locationNumber={value.LocationNumber}
                            key={index}
                            isSelected={value === selectedPatchLocation}
                            recentlyPatched={recentlyPatchedLocations.includes(value)}
                            onClick={() => setSelectedPatchLocation(value)}
                            style={value.PositionInfo}
                            disabled={props.isViewOnly}
                            data-testid={`patch-location-selector-${index + 1}`}
                        >
                            {value.LocationNumber}
                        </PatchLocationButton>
                    ))}
                </PatchBodyBgImage>
                {selectedPatchLocation && (
                    <PatchAreaSelection justify="center" padding={'10px'}>
                        {selectedPatchLocation.PatchArea?.toUpperCase()} {props.isViewOnly ? 'APPLIED' : 'SELECTED'}
                    </PatchAreaSelection>
                )}
            </Layout>
        </>
    );
}


function getLabelText(label: PatchApplicationActivityTypeDto | string| undefined) {
    if (!label) {
        return "";
    }
    return label.replace(/([A-Z])/g, " $1").trimStart();
}
function ApplicationStatusSection(props: {
    isUpdateOrRemovePatch?: boolean;
    isNew?: boolean;
    currentStatus?: PatchApplicationStatusDto;
    activityType?: PatchApplicationActivityTypeDto;
    formControl: Control<PatchApplicationFormFields>;
    canReapply: boolean;
}) {
    const statuses:  PatchApplicationActivityTypeDto[] = [];

    if (!props.currentStatus || props.isNew) {
        statuses.push(PatchApplicationActivityTypeDto.Applied);
    }
    else if (props.currentStatus === PatchApplicationStatusDto.Applied || props.currentStatus === PatchApplicationStatusDto.Reapplied) {
        if (props.activityType === PatchApplicationActivityTypeDto.Sighted) {
            statuses.push(PatchApplicationActivityTypeDto.Sighted);
        }
        if (props.activityType !== PatchApplicationActivityTypeDto.Removed && props.canReapply) {
            statuses.push(PatchApplicationActivityTypeDto.FallenOff);
        }
        else if (props.canReapply) {
            statuses.push(PatchApplicationActivityTypeDto.Removed);
        }        
    } 
    else if (props.currentStatus === PatchApplicationActivityTypeDto.FallenOff) {
        statuses.push(PatchApplicationActivityTypeDto.Reapplied);
    }

    const statusOptions = statuses.map((status) => {
        return { value: status, label: getLabelText(status) };
    });

    return (
        <>
            {props.isNew && (
                <Layout gap={1}>
                    <Text size="large">APPLY</Text>
                </Layout>
            )}
            {!props.isNew && !props.isUpdateOrRemovePatch && (
                <Layout gap={1}>
                    <Text size="large">Patch {getLabelText(props.activityType)}</Text>
                </Layout>
            )}
            {!props.isNew && props.isUpdateOrRemovePatch && (
                <Layout gap={1}>
                    <Text size="large">Update status</Text>
                    <RadioGroup name="activityType" options={statusOptions} cols={3} control={props.formControl} required value={props.currentStatus}/>
                </Layout>
            )}
        </>
    );
}

function PatchNotes(props: { isHidden?: boolean; isRecentlyPatched: boolean, activityType?: PatchApplicationActivityTypeDto }) {
    return (
        <FormGroup fullWidth>
            <TextArea
                name="notes"
                placeholder="Add note..."
                rows={3}
                disabled={!!props.isHidden}
                fullWidth
                rules={{
                    validate: (notes: string) => {
                        if (props.isRecentlyPatched && notes.length === 0 && (!props.activityType || props.activityType === PatchApplicationActivityTypeDto.Applied || props.activityType === PatchApplicationActivityTypeDto.Reapplied)) {
                            return 'A patch has been applied to this location recently. A note is required if you apply the patch here again.';
                        }
                        return true;
                    },
                }}
            />
        </FormGroup>
    );
}

const DialogContainer = styled(Layout)`
  padding: 1em;
  grid-template-columns: 3fr 5fr;
`;

const WhiteText = styled(Text)`
  color: ${(props) => props.theme.backgrounds.default.fg};
`;

const ActionButton = styled(Button)`
  min-width: 10rem;
  &:disabled {
    cursor: not-allowed;
  }
`;

const PatchAreaSelection = styled(Layout)`
  background-color: ${colors.bg_purple_400};
`;
